import React,
{
  useState,
  useEffect,
  createContext,
  Dispatch,
  SetStateAction,
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  FormEvent,
} from 'react';
import { getParamFromUrl } from '@fff-web/fff-utilities';
import { getCampaignInfo } from '../../../utils/Data/customizeConnector';
import { PlanCode } from '../../../utils/planCodes';
import {
  CampaignInfo,
  Country,
  createEmptyCampaignInfo,
  createEmptyProspect,
  ExperienceFlow,
  Prospect,
} from './SharedContextTypes';
import { useGetCountries } from '../../../hooks/useGetCountries';
import EXPERIENCE_FLOW from '../../../utils/constants/experienceFlow';

export interface IErrorPricePreview {
  message: string;
  code: 'INVALID_ADDRESS'
  | 'INVALID_REDEMPTION_CODE'
  | 'DUPLICATE_SUBSCRIPTION'
  | 'INVALID_SKU'
  | 'BAD_INPUT'
  | 'UNKNOWN_ERROR';
}

export type FormStateChange = {
  target: {
    name: string;
    value: string;
  }
  [key: string]: any
};

export type PreviewErrorProps = IErrorPricePreview | null | undefined;

type SharedContextProps = {
  planCode: PlanCode;
  setPlanCode: Dispatch<SetStateAction<PlanCode>>;
  promoCode: string;
  setPromoCode: Dispatch<SetStateAction<string>>;
  prospect: Prospect;
  setProspect: Dispatch<SetStateAction<Prospect>>;
  timerStatus: string,
  setTimerStatus: Dispatch<SetStateAction<string>>;
  isRecurlyFormValid: boolean;
  setIsRecurlyFormValid: Dispatch<SetStateAction<boolean>>,
  handleFormStateOnChange: (event: FormChangeEvent) => void;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  initialLoading: boolean;
  setInitialLoading: Dispatch<SetStateAction<boolean>>;
  campaign: CampaignInfo;
  experienceFlow: ExperienceFlow;
  countries: Country[];
  submitText: string;
  setSubmitText: Dispatch<SetStateAction<string>>;
  setPreviewError: Dispatch<SetStateAction<IErrorPricePreview | null>>;
  previewError: IErrorPricePreview | null;
  checkoutTotal: number;
  setCheckoutTotal: Dispatch<SetStateAction<number>>;
};

export const SharedContext = createContext<SharedContextProps | undefined>(undefined);

type Props = {
  children: any;
  experienceFlow: ExperienceFlow;
};

export type FormChangeEvent = ChangeEvent<HTMLSelectElement | HTMLInputElement>
| FocusEvent<HTMLInputElement>
| KeyboardEvent<HTMLInputElement>
| FormEvent
| FormStateChange;

const SharedProvider = ({ children, experienceFlow }: Props) => {
  const isPublicShop = experienceFlow === EXPERIENCE_FLOW.publicShop;
  const orderButton = isPublicShop ? 'Place Order' : 'Order the Box';
  const urlPlanCode = getParamFromUrl('planCode') as PlanCode;
  const countries = useGetCountries();
  const [planCode, setPlanCode] = useState<PlanCode>(urlPlanCode);
  const [promoCode, setPromoCode] = useState('');
  const [prospect, setProspect] = useState(createEmptyProspect());
  const [timerStatus, setTimerStatus] = useState('active');
  const [isRecurlyFormValid, setIsRecurlyFormValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [campaign, setCampaign] = useState(createEmptyCampaignInfo());
  const [previewError, setPreviewError] = useState<IErrorPricePreview | null>(null);
  const [submitText, setSubmitText] = useState(orderButton);
  const [checkoutTotal, setCheckoutTotal] = useState(0);

  const handleFormStateOnChange = (event: FormChangeEvent) => {
    const { name, value } = event.target as (HTMLSelectElement | HTMLInputElement);
    const sanitizedValue = value.trim();
    const { address } = prospect;
    let updatedProspect = { ...prospect };

    if (name === 'shipCountry') {
      updatedProspect = {
        ...prospect,
        address: {
          ...address,
          shipCountry: sanitizedValue,
          shipState: '',
        },
      };
    } else if (name === 'shipZipCode' || name === 'shipState') {
      updatedProspect = {
        ...prospect,
        address: { ...address, [name]: sanitizedValue },
      };
    } else {
      updatedProspect = { ...prospect, [name]: sanitizedValue };
    }
    setProspect(() => ({ ...updatedProspect }));
  };

  useEffect(() => {
    const urlPromoCode = getParamFromUrl('coupon_code');
    setPromoCode(urlPromoCode);
  }, []);

  useEffect(() => {
    getCampaignInfo()
      .then((response) => {
        const { campaign: campaignInfo } = response.data;
        const { id, title } = campaignInfo;
        setCampaign({ campaignId: id, campaignName: title });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
  }, []);

  return (
    <SharedContext.Provider value={{
      planCode,
      setPlanCode,
      promoCode,
      setPromoCode,
      prospect,
      setProspect,
      timerStatus,
      setTimerStatus,
      isRecurlyFormValid,
      setIsRecurlyFormValid,
      handleFormStateOnChange,
      loading,
      setLoading,
      initialLoading,
      setInitialLoading,
      campaign,
      experienceFlow,
      countries,
      submitText,
      setSubmitText,
      previewError,
      setPreviewError,
      checkoutTotal,
      setCheckoutTotal,
    }}
    >
      {children}
    </SharedContext.Provider>
  );
};

export function useSharedContext() {
  const context = React.useContext(SharedContext);
  if (!context) {
    throw new Error(
      'You must use the SharedContext Provider in order to consume this context.',
    );
  }
  return context;
}

export default SharedProvider;
